import { Controller } from "@hotwired/stimulus";

interface ActionLink {
  label?: string;
  url?: string;
  divider?: boolean;
  modal?: "jquery" | "turbo";
  method?: string;
  confirm?: string;
  warningModal?: { title: string; message: string; confirmLabel?: string };
  checkProxy?: boolean;
}

interface NavLink {
  label: string;
  url: string;
  disabled?: boolean;
  tooltip?: string;
  modal?: "jquery" | "turbo";
  checkProxy?: boolean;
  current?: boolean;
}

export default class extends Controller {
  static values = { actionLinks: Array, navigationLinks: Array, pageTitle: String, toolbarNotice: String, secureTallyUrl: String };
  static targets = ["navBar"];

  declare readonly actionLinksValue: ActionLink[];
  declare readonly navigationLinksValue: NavLink[];
  declare readonly pageTitleValue: string;
  declare readonly toolbarNoticeValue: string;
  declare readonly secureTallyUrlValue: string;
  declare readonly navBarTarget: HTMLElement;
  declare readonly hasNavBarTarget: boolean;

  private injectedActionsWrapper: HTMLElement | null = null;
  private injectedNavWrapper: HTMLElement | null = null;
  private injectedNoticeDesktop: HTMLElement | null = null;
  private injectedNoticeMobile: HTMLElement | null = null;
  private actionsBtn: HTMLButtonElement | null = null;
  private actionsMenu: HTMLElement | null = null;
  private pageNavBtn: HTMLButtonElement | null = null;
  private pageNavDropdown: HTMLElement | null = null;
  private outsideClickHandler: ((e: MouseEvent) => void) | null = null;
  private escapeHandler: ((e: KeyboardEvent) => void) | null = null;

  connect() {
    if (this.navigationLinksValue.length > 0) {
      this.buildAndInjectPageNav();
    }
    if (this.actionLinksValue.length > 0) {
      this.buildAndInjectActionMenu();
    }
    if (this.toolbarNoticeValue) {
      this.buildAndInjectToolbarNotice();
    }

    this.outsideClickHandler = (e: MouseEvent) => {
      if (this.actionsMenu && !this.actionsMenu.hidden) {
        if (
          !this.actionsBtn?.contains(e.target as Node) &&
          !this.actionsMenu.contains(e.target as Node)
        ) {
          this.close();
        }
      }
      if (this.pageNavDropdown && !this.pageNavDropdown.hidden) {
        if (
          !this.pageNavBtn?.contains(e.target as Node) &&
          !this.pageNavDropdown.contains(e.target as Node)
        ) {
          this.closePageNav();
        }
      }
    };
    document.addEventListener("click", this.outsideClickHandler);

    this.escapeHandler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (this.actionsMenu && !this.actionsMenu.hidden) this.close();
        if (this.pageNavDropdown && !this.pageNavDropdown.hidden) this.closePageNav();
      }
    };
    document.addEventListener("keydown", this.escapeHandler);
  }

  disconnect() {
    this.injectedActionsWrapper?.remove();
    this.injectedActionsWrapper = null;
    this.injectedNavWrapper?.remove();
    this.injectedNavWrapper = null;
    this.injectedNoticeDesktop?.remove();
    this.injectedNoticeDesktop = null;
    this.injectedNoticeMobile?.remove();
    this.injectedNoticeMobile = null;
    this.actionsBtn = null;
    this.actionsMenu = null;
    this.pageNavBtn = null;
    this.pageNavDropdown = null;

    if (this.outsideClickHandler) {
      document.removeEventListener("click", this.outsideClickHandler);
      this.outsideClickHandler = null;
    }
    if (this.escapeHandler) {
      document.removeEventListener("keydown", this.escapeHandler);
      this.escapeHandler = null;
    }
  }

  private buildAndInjectToolbarNotice() {
    const grid = this.element.nextElementSibling;
    if (!grid) return;

    const makeNotice = (extraClass: string) => {
      const el = document.createElement("span");
      el.className = `result-header__toolbar-notice ${extraClass}`;
      el.innerHTML = this.toolbarNoticeValue;
      return el;
    };

    // Desktop: inject between title-group and search-row inside the quick-filter-bar
    const titleGroup = grid.querySelector(".ag-grid-component-title-group");
    if (titleGroup?.parentElement) {
      this.injectedNoticeDesktop = makeNotice("result-header__toolbar-notice--desktop");
      titleGroup.parentElement.insertBefore(this.injectedNoticeDesktop, titleGroup.nextSibling);
    }

    // Mobile: inject after the entire quick-filter-bar, before the grid
    const quickFilterBar = grid.querySelector(".ag-grid-component-quick-filter-bar");
    if (quickFilterBar?.parentElement) {
      this.injectedNoticeMobile = makeNotice("result-header__toolbar-notice--mobile");
      quickFilterBar.parentElement.insertBefore(this.injectedNoticeMobile, quickFilterBar.nextSibling);
    }
  }

  private buildAndInjectPageNav() {
    const wrapper = document.createElement("div");
    wrapper.className = "ag-grid-component-title-nav";

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "ag-grid-component-title-nav__btn";
    btn.setAttribute("aria-haspopup", "true");
    btn.setAttribute("aria-expanded", "false");
    btn.innerHTML = `${this.pageTitleValue} <span class="ag-grid-component-title-nav__arrow" aria-hidden="true">&#9660;</span>`;
    btn.addEventListener("click", () => this.togglePageNav());
    this.pageNavBtn = btn;

    const dropdown = document.createElement("ul");
    dropdown.className = "ag-grid-component-title-nav__dropdown";
    dropdown.hidden = true;

    for (const link of this.navigationLinksValue) {
      if (link.current) continue;
      const li = document.createElement("li");
      if (link.disabled) {
        const span = document.createElement("span");
        span.className = "ag-grid-component-title-nav__link--disabled";
        span.dataset.tooltip = this.formatTooltip(link.tooltip ?? "");
        span.textContent = link.label;
        li.appendChild(span);
      } else {
        const a = document.createElement("a");
        a.href = link.url;
        a.textContent = link.label;
        if (link.modal === "jquery") a.rel = "modal:open";
        if (link.modal === "turbo") {
          a.dataset.turbo = "true";
          a.dataset.turboFrame = "modal";
        }
        if (link.checkProxy && this.secureTallyUrlValue) {
          a.addEventListener("click", (e) => {
            e.preventDefault();
            this.checkProxyAndRedirect(link.url);
          });
        }
        li.appendChild(a);
      }
      dropdown.appendChild(li);
    }
    this.pageNavDropdown = dropdown;

    wrapper.appendChild(btn);
    wrapper.appendChild(dropdown);
    this.injectedNavWrapper = wrapper;

    // Inject into AgGrid title group if present, else show in fallback nav-bar
    const titleGroup = this.element.nextElementSibling?.querySelector(".ag-grid-component-title-group");
    if (titleGroup) {
      titleGroup.prepend(wrapper);
    } else if (this.hasNavBarTarget) {
      this.navBarTarget.hidden = false;
      this.navBarTarget.appendChild(wrapper);
    }
  }

  private formatTooltip(tooltip: string) {
    return tooltip.replace(
      "ends due to security settings",
      "ends\ndue to security settings"
    );
  }

  private buildAndInjectActionMenu() {
    const wrapper = document.createElement("div");
    wrapper.className = "result-header__actions-wrapper";

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "result-header__actions-btn";
    btn.setAttribute("aria-haspopup", "true");
    btn.setAttribute("aria-expanded", "false");
    btn.innerHTML = `Actions <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>`;
    btn.addEventListener("click", () => this.toggle());
    this.actionsBtn = btn;

    const dropdown = document.createElement("div");
    dropdown.className = "result-header__actions-dropdown";
    dropdown.hidden = true;

    for (const link of this.actionLinksValue) {
      if (link.divider) {
        const hr = document.createElement("hr");
        hr.className = "result-header__actions-divider";
        dropdown.appendChild(hr);
      } else {
        const a = document.createElement("a");
        a.href = link.url ?? "#";
        a.className = "result-header__actions-option";
        a.textContent = link.label ?? "";
        if (link.modal === "jquery") a.rel = "modal:open";
        else if (link.modal === "turbo") {
          a.dataset.turbo = "true";
          a.dataset.turboFrame = "modal";
        }
        if (link.checkProxy && this.secureTallyUrlValue) {
          a.addEventListener("click", (e) => {
            e.preventDefault();
            this.close();
            this.checkProxyAndRedirect(link.url!);
          });
        } else if (link.warningModal) {
          a.addEventListener("click", (e) => {
            e.preventDefault();
            this.close();
            this.showWarningModal(
              [{ text: link.warningModal!.message, red: true }],
              link.warningModal!.confirmLabel ?? "OK",
              () => this.submitWithMethod(link.url!, link.method ?? "get"),
              { title: link.warningModal!.title, confirmFirst: true }
            );
          });
        } else {
          if (link.method) a.dataset.method = link.method;
          if (link.confirm) a.dataset.confirm = link.confirm;
        }
        dropdown.appendChild(a);
      }
    }
    this.actionsMenu = dropdown;

    wrapper.appendChild(btn);
    wrapper.appendChild(dropdown);
    this.injectedActionsWrapper = wrapper;

    const searchRow = this.element.nextElementSibling?.querySelector(".ag-grid-component-search-row");
    if (searchRow) {
      searchRow.prepend(wrapper);
    } else if (this.hasNavBarTarget) {
      this.navBarTarget.hidden = false;
      this.navBarTarget.appendChild(wrapper);
    } else {
      wrapper.classList.add("result-header__actions-inline");
      this.element.appendChild(wrapper);
    }
  }

  private togglePageNav() {
    if (!this.pageNavDropdown) return;
    if (this.pageNavDropdown.hidden) {
      this.pageNavDropdown.hidden = false;
      this.pageNavBtn?.setAttribute("aria-expanded", "true");
    } else {
      this.closePageNav();
    }
  }

  private closePageNav() {
    if (!this.pageNavDropdown) return;
    this.pageNavDropdown.hidden = true;
    this.pageNavBtn?.setAttribute("aria-expanded", "false");
  }

  private toggle() {
    if (!this.actionsMenu) return;
    if (this.actionsMenu.hidden) {
      this.actionsMenu.hidden = false;
      this.actionsBtn?.setAttribute("aria-expanded", "true");
    } else {
      this.close();
    }
  }

  private close() {
    if (!this.actionsMenu) return;
    this.actionsMenu.hidden = true;
    this.actionsBtn?.setAttribute("aria-expanded", "false");
  }

  private checkProxyAndRedirect(targetHref: string) {
    fetch(this.secureTallyUrlValue, { headers: { Accept: "application/json" } })
      .then((r) => r.json())
      .then((data) => {
        if (data.allow_redirect) {
          window.location.href = targetHref;
        } else if (data.low_security_with_proxy) {
          this.showProxyModal(targetHref);
        } else {
          this.showBallotModal(data.votes_remaining, targetHref, data.proxy_opened);
        }
      });
  }

  private showProxyModal(targetHref: string) {
    this.showWarningModal(
      [{ text: "This vote has proxy vote active. If the results are viewed, the administrator won't be able to record the proxy vote.", red: true }],
      "Close Proxy Entry and View Results",
      () => { window.location.href = targetHref; }
    );
  }

  private showBallotModal(votesRemaining: number, targetHref: string, proxyOpened: boolean) {
    const messages: Array<{ text: string; red?: boolean }> = [
      { text: `${votesRemaining} voters have not voted. These voters may submit paper ballots.` },
      { text: "If results are viewed, no more paper ballots can be recorded by administrators", red: true },
    ];
    if (proxyOpened) {
      messages.push({ text: "This vote has proxy vote active. If the results are viewed, the administrator won't be able to record the proxy vote.", red: true });
    }
    this.showWarningModal(messages, "Close Paper Ballot Entry and View Results", () => { window.location.href = targetHref; });
  }

  private showWarningModal(
    messages: Array<{ text: string; red?: boolean }>,
    actionLabel: string,
    onConfirm: () => void,
    { title = "Warning", confirmFirst = false }: { title?: string; confirmFirst?: boolean } = {}
  ) {
    document.getElementById("proxy-warning-modal-overlay")?.remove();
    document.getElementById("proxy-warning-modal-style")?.remove();

    const style = document.createElement("style");
    style.id = "proxy-warning-modal-style";
    style.textContent = `
      @media (max-width: 620px) {
        #proxy-warning-modal-overlay .toolkit-confirm-delete-modal__actions {
          flex-direction: column;
        }
        #proxy-warning-modal-overlay .toolkit-confirm-delete-modal__actions > * {
          width: 100%;
          white-space: normal;
        }
      }
    `;
    document.head.appendChild(style);

    const overlay = document.createElement("div");
    overlay.id = "proxy-warning-modal-overlay";
    overlay.className = "toolkit-confirm-delete-modal__overlay";

    const dialog = document.createElement("div");
    dialog.className = "toolkit-confirm-delete-modal__dialog";
    dialog.style.maxWidth = "620px";
    dialog.setAttribute("role", "alertdialog");
    dialog.setAttribute("aria-modal", "true");

    const content = document.createElement("div");
    content.className = "toolkit-confirm-delete-modal__content";

    const iconWrap = document.createElement("div");
    iconWrap.className = "toolkit-confirm-delete-modal__icon toolkit-confirm-delete-modal__icon--warning";
    iconWrap.innerHTML = `<i class="fas fa-exclamation-triangle"></i>`;

    const titleEl = document.createElement("h3");
    titleEl.className = "toolkit-confirm-delete-modal__title";
    titleEl.textContent = title;

    content.appendChild(iconWrap);
    content.appendChild(titleEl);

    for (const msg of messages) {
      const p = document.createElement("p");
      p.className = "toolkit-confirm-delete-modal__message";
      p.style.fontSize = "1.5rem";
      if (msg.red) p.style.cssText = "color: #dc2626; font-weight: 500; font-size: 1.5rem;";
      const lines = msg.text.split("\n");
      lines.forEach((line, i) => {
        p.appendChild(document.createTextNode(line));
        if (i < lines.length - 1) p.appendChild(document.createElement("br"));
      });
      content.appendChild(p);
    }

    const actions = document.createElement("div");
    actions.className = "toolkit-confirm-delete-modal__actions";

    const closeModal = () => { overlay.remove(); style.remove(); };

    const cancelBtn = document.createElement("button");
    cancelBtn.className = "toolkit-button-component toolkit-button-component--blue-stroke";
    cancelBtn.type = "button";
    cancelBtn.textContent = "Cancel";
    cancelBtn.addEventListener("click", closeModal);

    const confirmBtn = document.createElement("button");
    confirmBtn.className = "toolkit-button-component toolkit-button-component--orange";
    confirmBtn.type = "button";
    confirmBtn.textContent = actionLabel;
    confirmBtn.addEventListener("click", () => { closeModal(); onConfirm(); });

    if (confirmFirst) {
      actions.appendChild(confirmBtn);
      actions.appendChild(cancelBtn);
    } else {
      actions.appendChild(cancelBtn);
      actions.appendChild(confirmBtn);
    }

    content.appendChild(actions);
    dialog.appendChild(content);
    overlay.appendChild(dialog);

    overlay.addEventListener("click", (e) => { if (e.target === overlay) closeModal(); });

    document.body.appendChild(overlay);
  }

  private submitWithMethod(url: string, method: string) {
    const form = document.createElement("form");
    form.action = url;
    form.method = ["get", "post"].includes(method.toLowerCase()) ? method.toLowerCase() : "post";

    const csrf = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null;
    if (csrf) {
      const tokenInput = document.createElement("input");
      tokenInput.type = "hidden";
      tokenInput.name = "authenticity_token";
      tokenInput.value = csrf.content;
      form.appendChild(tokenInput);
    }

    if (!["get", "post"].includes(method.toLowerCase())) {
      const methodInput = document.createElement("input");
      methodInput.type = "hidden";
      methodInput.name = "_method";
      methodInput.value = method;
      form.appendChild(methodInput);
    }

    document.body.appendChild(form);
    form.submit();
  }
}
